import { useState, useEffect, useCallback, useMemo } from "react";

import { useNavigate } from "react-router-dom";

//Props Validations
import PropTypes from "prop-types";

// import component
import Loader from "../../common/Loader";
import FilterAcrossProductsToggleComponent from "../../common/FilterAcrossProductsToggleComponent";
import Reset from "../../common/Reset";
import TTestDialog from "../../common/TTest/TTestDialog";
import AnalysisTypeSelector from "../../common/AnalysisTypeSelector";
import ViewToggle from "../../common/ViewToggle";
import InitialPopup from "../../common/InitialPopup";
import CohensDButton from "components/common/CohensDButton";
import SignificanceToggleComponent from "components/common/SignificanceToggleComponent";
import AnalysisTableTransposed from "../../common/analysisModule/AnalysisTableTransposed";
import AnalysisTransposedTableChart from "../../common/analysisModule/chart/AnalysisTransposedTableChart";
import DataPointSampling from "components/common/DataPointSampling";
import ScoreDistributionVariableSelect from "components/common/ScoreDistributionVariableSelect";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
    loadIsFragranceMetricsAnalysisResultLoadingState,
    loadFragranceMetricsAnalysisResultState,
    loadDefaultFragranceMetricsAnalysisResultState,
    loadIsScoreDistributionShownAdditionalState,
    loadDisableSignificanceToggleState,
    loadSignificanceToggleState,
    loadScoreDistributionSelectedVariableAdditionalState,
    loadScoreDistributionGroupsAdditionalState,
} from "store/features/analysisResultsSlice";
import { resetTTestStore } from "store/features/ttestSlice";

// utils
import GetFragranceMetricsResult from "utils/apis/GetFragranceMetricsResult";
import { loadSecondaryRemovedKpiListState } from "store/features/analysisKpiSlice";

function AditionalAttributeTab({ dataMappingData = {} }) {
    const navigate = useNavigate();

    //REDUX
    const dispatch = useDispatch();
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);

    const productsSecondaryDataState = useSelector((state) => state.analysisResultsStore.fragranceMetricsAnalysisResult);
    const isLoading = useSelector((state) => state.analysisResultsStore.isFragranceMetricsAnalysisResultLoading);
    const weightToggle = useSelector((state) => state.analysisResultsStore.weightToggle);
    const isConsumerFilterSelected = useSelector((state) => state.consumerFilterStore.filterSelectionStatus);

    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    // toggle table and chart
    const [isTableShown, setIsTableShown] = useState(true);

    // t-test details
    const [tTestResult, setTTestResult] = useState([]);
    const [testLabel, setTestLabel] = useState("");

    // cohens d details
    const [cohensDResult, setCohensDResult] = useState([]);

    // Analysis type
    const [isFisher, setIsFisher] = useState(false);
    const [legends, setLegends] = useState([]);

    // data for data point sampling
    const [data, setData] = useState({});

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    // box analysis code
    const overallBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.overallBoxAnalysisSelection);
    const secondaryBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.secondaryBoxAnalysisSelection);
    const agreementBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.agreementBoxAnalysisSelection);
    const claimsBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.claimsBoxAnalysisSelection);
    const analysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.expressCltAdditionalTypeValue);
    const boxAnalysisTypeSelection = useSelector((state) => state.analysisTypeSelectionStore.expressCltAdditionalBoxAnalysisTypeSelection);
    const expressCltAdditionalBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.expressCltAdditionalBoxAnalysisSelection);
    const moduleName = "secondary";
    const categoriesToDisplay = ["Overall Fragrance", "Fragrance (Secondary)", "Sensory & Application"];
    const macroCategoryData = dataMappingData?.columns ?? [];
    const sortedItems = useMemo(() => {
        const orderIndex = new Map(
            categoriesToDisplay.map((k, i) => [k, i])
        );

        return (macroCategoryData ?? [])
            .slice()
            .sort((a, b) => {
                const ia = orderIndex.get(a.macroCategory) ?? Infinity;
                const ib = orderIndex.get(b.macroCategory) ?? Infinity;
                return ia - ib;
            })
            .map(category => ({
                ...category,
                displayLabels: category.displayLabels
                    ?.slice()
                    .sort((a, b) =>
                        a.displayLabel.localeCompare(b.displayLabel)
                    )
            }));
    }, [macroCategoryData]);

    const scoreDistributionSelectedVariableAdditional = useSelector((state) => state.analysisResultsStore.scoreDistributionSelectedVariableAdditional);
    const isScoreDistributionShownAdditional = useSelector((state) => state.analysisResultsStore.isScoreDistributionShownAdditional);
    const scoreDistributionGroupsAdditional = useSelector((state) => state.analysisResultsStore.scoreDistributionGroupsAdditional);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);

    const clearResult = useCallback(() => {
        setTTestResult([]);
        setTestLabel("");
        dispatch(resetTTestStore());
        dispatch(loadSignificanceToggleState(false));
        dispatch(loadDisableSignificanceToggleState(true));
        setCohensDResult([]);
        dispatch(loadScoreDistributionSelectedVariableAdditionalState(""));
        dispatch(loadScoreDistributionGroupsAdditionalState([]));
        dispatch(loadIsScoreDistributionShownAdditionalState(true));
        setData({});
    }, []);

    // clear T Test
    useEffect(() => {
        clearResult();
    }, [weightToggle]);

    useEffect(() => {
        if (isOralCare || isSkinCleansing) {
            if (!isLoading) {
                if (!isConsumerFilterSelected) {
                    InitialPopup.general();
                }
            }
        }
    }, [isLoading, isConsumerFilterSelected, isOralCare, isSkinCleansing]);

    useEffect(() => {
        dispatch(loadSecondaryRemovedKpiListState(["R_OO_Frag_10pt"]));
    }, ["R_OO_Frag_10pt"]);

    const clearTtest = (product, type) => {
        // clear t test
        if (type === "consumerFilterAll") {
            clearResult();
        } else if (type === "consumerFilter") {
            const tTestPro = tTestResult?.[0]?.colors?.map((datum) => datum.productCode) || [];
            const cfPc = product?.split("_")?.join(" ") || "";
            if (tTestPro.indexOf(cfPc) !== -1) {
                clearResult();
            }
        } else {
            clearResult();
        }
    };

    const getAditionalResult = async ({ product = null, coreDataId = null, type = null, consumerFilter = null, pcs = [], selectedStudy = [], allAnalysisData = {}, fetchOnlyCurrent = false, boxAnalysisSelection = {} }) => {
        clearTtest(product, type);
        dispatch(loadIsFragranceMetricsAnalysisResultLoadingState(true));

        const fragranceMetricsResult = await GetFragranceMetricsResult({
            product,
            coreDataId,
            type,
            consumerFilter,
            pcs,
            selectedStudy,
            productsSecondaryDataState: allAnalysisData?.fragranceMetricsAnalysisResult || [],
            productsOrder,
            productCategoryId,
            boxAnalysisSelection: boxAnalysisSelection["expressCltAdditional"] || {},
            studyTypeMethod: "expressClt",
        });
        if (fragranceMetricsResult) {
            await dispatch(loadFragranceMetricsAnalysisResultState([...fragranceMetricsResult]));
            if (!type) {
                dispatch(loadDefaultFragranceMetricsAnalysisResultState([...fragranceMetricsResult]));
            }
        }
        dispatch(loadIsFragranceMetricsAnalysisResultLoadingState(false));
        console.log("fetchOnlyCurrent", fetchOnlyCurrent);
    };

    useEffect(() => {
        async function fetchAnalysisResult() {
            // if (!productsSecondaryDataState.length) {
                await getAditionalResult({
                    product: null,
                    coreDataId: null,
                    type: null,
                    consumerFilter: null,
                    pcs: [],
                    selectedStudy,
                    allAnalysisData: [],
                    fetchOnlyCurrent: true,
                    boxAnalysisSelection: {
                        overall: overallBoxAnalysisSelection,
                        secondary: secondaryBoxAnalysisSelection,
                        agreement: agreementBoxAnalysisSelection,
                        claims: claimsBoxAnalysisSelection,
                        expressCltAdditional: expressCltAdditionalBoxAnalysisSelection,
                    },
                });
            // }
        }

        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            fetchAnalysisResult();
        }
    }, [selectedStudy]);

    const returnComponent = () => {
        if (Object.keys(dataMappingData).length !== 0 && dataMappingData?.columns) {
            if (isTableShown) {
                return (
                    <AnalysisTableTransposed
                        productsSecondaryData={productsSecondaryDataState}
                        handleFilterSubmit={getAditionalResult}
                        selectedStudy={selectedStudy}
                        analysisLevelData={{
                            tableConfig: {
                                stickyColumns: ["Products", "Sample Size", "Consumer Filters"],
                            },
                            moduleName: moduleName,
                            bottomLabelModuleName: "fragranceMetrics",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: expressCltAdditionalBoxAnalysisSelection,
                            mappings: sortedItems?.filter((col) => categoriesToDisplay.includes(col.macroCategory)) || [],
                            // hideVariable: "R_OO_Frag_10pt",
                            cohensDResult: cohensDResult,
                        }}
                        tTest={{
                            tTestLegends: legends,
                            isFisher: isFisher,
                            tTestResult: tTestResult,
                            testLabel: testLabel,
                        }}
                        isTableShown={isTableShown}
                    />
                );
            } else {
                return (
                    <AnalysisTransposedTableChart
                        productsSecondaryData={productsSecondaryDataState}
                        handleFilterSubmit={getAditionalResult}
                        selectedStudy={selectedStudy}
                        analysisLevelData={{
                            tableConfig: {
                                stickyColumns: ["Products", "Sample Size", "Consumer Filters"],
                            },
                            moduleName: moduleName,
                            bottomLabelModuleName: "fragranceMetrics",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: expressCltAdditionalBoxAnalysisSelection,
                            mappings: dataMappingData?.columns?.filter((col) => categoriesToDisplay.includes(col.macroCategory)) || [],
                            isScoreDistributionShown: isScoreDistributionShownAdditional,
                            scoreDistributionGroups: scoreDistributionGroupsAdditional,
                            scoreDistributionSelectedVariable: scoreDistributionSelectedVariableAdditional,
                        }}
                        tTest={{
                            tTestLegends: legends,
                            isFisher: isFisher,
                            tTestResult: tTestResult,
                            testLabel: testLabel,
                        }}
                        isTableShown={isTableShown}
                    />
                );
            }
        }
        return null;
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="card-btns"
                style={{ padding: "0px 0px", margin: "10px 0px" }}
            >
                <div
                    className=" d-flex"
                    style={{ alignItems: "center" }}
                >
                    <div>
                        <ViewToggle
                            value={isTableShown}
                            setValue={setIsTableShown}
                            labels={["Table", "Chart"]}
                        />
                    </div>
                    <div
                        style={{
                            marginLeft: "1.5rem",
                            zIndex: "3",
                            width: "150px",
                        }}
                    >
                        <AnalysisTypeSelector
                            value={analysisTypeValue}
                            pageName="expressCLTAdditionalAnalysis"
                            variableCategory={"Fragrance Metrics Analysis"}
                            handleFilterSubmit={getAditionalResult}
                            mappings={dataMappingData?.columns?.filter((col) => categoriesToDisplay.includes(col.macroCategory)) || []}
                            moduleName="secondary"
                            clearResult={clearResult}
                            disabled={!isTableShown && !isScoreDistributionShownAdditional && selectedStudy.length === 1 && analysisTypeValue.value === "mean"}
                        />
                    </div>
                    {!isTableShown && !isScoreDistributionShownAdditional && selectedStudy.length === 1 && analysisTypeValue.value === "mean" && (
                        <>
                            <div
                                style={{
                                    marginLeft: "1.5rem",
                                    zIndex: "3",
                                    width: "150px",
                                }}
                            >
                                <ScoreDistributionVariableSelect
                                    dataMappingData={dataMappingData?.columns?.filter((col) => categoriesToDisplay.includes(col.macroCategory)) || []}
                                    productsSecondaryDataState={productsSecondaryDataState}
                                    scoreDistributionSelectedVariable={scoreDistributionSelectedVariableAdditional}
                                    moduleName="secondary"
                                />
                            </div>
                            <div
                                style={{
                                    marginLeft: "1.5rem",
                                }}
                            >
                                <DataPointSampling
                                    likertScaleData={likertScaleData}
                                    scoreDistributionGroups={scoreDistributionGroupsAdditional}
                                    scoreDistributionSelectedVariable={scoreDistributionSelectedVariableAdditional}
                                    moduleName="secondary"
                                    data={data}
                                    setData={setData}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div>
                    <SignificanceToggleComponent disabled={(!isTableShown && !isScoreDistributionShownAdditional && selectedStudy.length === 1 && analysisTypeValue.value === "mean") || !isTableShown} />
                    <FilterAcrossProductsToggleComponent />
                    <CohensDButton
                        analysisLevelData={{
                            moduleName: "secondary",
                            dataMappingData: dataMappingData?.columns?.filter((col) => categoriesToDisplay.includes(col.macroCategory)) || [],
                            analysisTypeValue: analysisTypeValue,
                        }}
                        tabData={{
                            productsSecondaryDataState: productsSecondaryDataState,
                        }}
                        setCohensDResult={setCohensDResult}
                        disabled={!isTableShown && !isScoreDistributionShownAdditional && selectedStudy.length === 1 && analysisTypeValue.value === "mean"}
                    />
                    <TTestDialog
                        variableCategory="Fragrance Metrics Analysis"
                        module = "expressClt"
                        analysisLevelData={{
                            moduleName: "secondary",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: expressCltAdditionalBoxAnalysisSelection,
                            mappings: sortedItems?.filter((col) => categoriesToDisplay.includes(col.macroCategory)) || [],
                        }}
                        functionsSettings={{
                            setIsFisher: setIsFisher,
                            setLegends: setLegends,
                            setTTestResult: setTTestResult,
                            setTestLabel: setTestLabel,
                            clearResult: clearResult,
                            // onTTestDataReceived: onTTestDataReceived,
                        }}
                        tabData={{
                            productsSecondaryDataState: productsSecondaryDataState,
                        }}
                        tTestBtnDisabled={!isTableShown && !isScoreDistributionShownAdditional && selectedStudy.length === 1 && analysisTypeValue.value === "mean"}
                    />
                    <Reset clearTTestResult={clearResult} />
                </div>
            </div>
            <div
                style={{
                    minHeight: "calc(100vh - 300px)",
                }}
            >
                {returnComponent()}
            </div>
            {!isTableShown && selectedStudy.length === 1 && analysisTypeValue.value === "mean" && (
                <div
                    style={{
                        width: "200px",
                        marginTop: "12px",
                    }}
                >
                    <ViewToggle
                        value={isScoreDistributionShownAdditional}
                        setValue={(value) => dispatch(loadIsScoreDistributionShownAdditionalState(value))}
                        labels={["Mean", "% Score Distribution"]}
                    />
                </div>
            )}
            {cohensDResult.length > 0 && <p style={{ fontSize: "12px", marginTop: "6px" }}>Note: Cohen&apos;s d is not visually represented in the chart view</p>}
        </>
    );
}

// Typechecking props for the SecondaryAttributeTab
AditionalAttributeTab.propTypes = {
    dataMappingData: PropTypes.object,
    // onTTestDataReceived: PropTypes.func,
};
export default AditionalAttributeTab;
